import React, { FC, useState } from "react"
import { useNavigate } from "react-router-dom"
import { KButton, KSpan } from "kahuna-base-react-components"

import { lang } from "../../../constants/languages"
import { CoManagerReleasePlan, CoManagerReleasePlanStepState, ReleasePlanActiveRelease } from "../../../types"
import { COMING_SOON_STEPS, STATE_COLORS, stepPath, stepState } from "../co-manager/ReleasePlanPanel"

type CalendarEntry = {
  key: string
  title: string
  // The step key, or "release" for the release day itself.
  kind: string
  state: CoManagerReleasePlanStepState | "release"
  path: string
}

const RELEASE_COLOR = "#111"
// Monday first, as the calendars the artists use in Turkey and Germany are.
const WEEKDAY_OFFSET = 6

const isoDay = (day: Date) =>
  `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`

/**
 * A month of the plans: each release day and each step on the day it is due, coloured by how it is
 * doing, with the active releases that have no plan in it as well. Clicking an entry opens where the
 * step is done, the same place the list's "Open" goes to.
 */
const ReleasePlanCalendar: FC<{
  plans: CoManagerReleasePlan[]
  activeReleases: ReleasePlanActiveRelease[]
}> = ({ plans, activeReleases }) => {
  const navigate = useNavigate()
  const words = lang.co_manager
  const stepWords = words.release_plan_steps as { [key: string]: string }
  const today = new Date()
  const [month, setMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1))

  const entries: { [day: string]: CalendarEntry[] } = {}
  const add = (day: string, entry: CalendarEntry) => (entries[day] = [...(entries[day] || []), entry])

  plans.forEach((plan) => {
    add(plan.releaseDate, {
      key: `plan-${plan.id}-release`,
      title: plan.title,
      kind: "release",
      state: "release",
      path: stepPath("submit", plan.releaseId, plan.releaseStatus)
    })
    plan.steps.forEach((step) =>
      add(step.dueDate, {
        key: `plan-${plan.id}-${step.key}`,
        title: plan.title,
        kind: step.key,
        state: stepState(step),
        path: stepPath(step.key, plan.releaseId, plan.releaseStatus)
      })
    )
  })
  activeReleases.forEach((release) => {
    add(release.releaseDate, {
      key: `release-${release.id}-release`,
      title: release.title,
      kind: "release",
      state: "release",
      path: stepPath("submit", release.id, release.releaseStatus)
    })
    release.steps.forEach((step) =>
      add(step.dueDate, {
        key: `release-${release.id}-${step.key}`,
        title: release.title,
        kind: step.key,
        state: stepState(step),
        path: stepPath(step.key, release.id, release.releaseStatus)
      })
    )
  })

  const first = new Date(month.getFullYear(), month.getMonth(), 1)
  const lead = (first.getDay() + WEEKDAY_OFFSET) % 7
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate()
  const cells = Array.from({ length: Math.ceil((lead + daysInMonth) / 7) * 7 }, (_, index) => {
    const dayNumber = index - lead + 1
    return dayNumber < 1 || dayNumber > daysInMonth ? null : new Date(month.getFullYear(), month.getMonth(), dayNumber)
  })

  const monthTitle = month.toLocaleDateString(words.release_plan_page_locale, { month: "long", year: "numeric" })
  const weekdays = Array.from({ length: 7 }, (_, index) =>
    // 2024-01-01 was a Monday.
    new Date(2024, 0, 1 + index).toLocaleDateString(words.release_plan_page_locale, { weekday: "short" })
  )

  const arrow = (text: string, change: number, dataTestId: string) => (
    <KButton
      dataTestId={dataTestId}
      text={text}
      onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + change, 1))}
      background="#F7F7F7"
      hoverBackground="#EFEFEF"
      borderRadius={100}
      width="32px"
      height="32px"
      padding="0px"
      fontSize={14}
      shadowDisabled
    />
  )

  return (
    <div className="release-plan-calendar">
      <div className="release-plan-calendar-head">
        {arrow("‹", -1, "release-plan-calendar-previous")}
        <div className="release-plan-calendar-title">
          <KSpan text={monthTitle} fontSize={16} lineHeight="22px" fontWeight={500} color="#111" />
        </div>
        {arrow("›", 1, "release-plan-calendar-next")}
      </div>
      <div className="release-plan-calendar-grid">
        {weekdays.map((weekday) => (
          <div className="release-plan-calendar-weekday" key={`weekday-${weekday}`}>
            <KSpan text={weekday} fontSize={11} lineHeight="14px" color="#999" />
          </div>
        ))}
        {cells.map((day, index) => {
          if (!day) return <div className="release-plan-calendar-cell is-empty" key={`empty-${index}`} />
          const iso = isoDay(day)
          const isToday = iso === isoDay(today)
          return (
            <div className={`release-plan-calendar-cell${isToday ? " is-today" : ""}`} key={iso}>
              <KSpan text={String(day.getDate())} fontSize={11} lineHeight="14px" color={isToday ? "#111" : "#999"} />
              {(entries[iso] || []).map((entry) => (
                <button
                  type="button"
                  className={`release-plan-calendar-entry${entry.kind === "release" ? " is-release" : ""}`}
                  key={entry.key}
                  title={`${entry.title} · ${
                    entry.kind === "release" ? words.release_plan_page_release_day : stepWords[entry.kind] || entry.kind
                  }`}
                  // A step that is not ready yet has nowhere to go: it stays on the calendar for its date only.
                  disabled={COMING_SOON_STEPS.includes(entry.kind)}
                  onClick={() => navigate(entry.path)}
                >
                  <span
                    className="co-manager-status-dot"
                    style={{
                      backgroundColor: entry.state === "release" ? RELEASE_COLOR : STATE_COLORS[entry.state],
                      flexShrink: 0
                    }}
                  />
                  <span className="release-plan-calendar-entry-text">
                    {entry.kind === "release" ? (
                      <span className="release-plan-calendar-entry-step">{entry.title}</span>
                    ) : (
                      <>
                        <span className="release-plan-calendar-entry-step">{stepWords[entry.kind] || entry.kind}</span>
                        {/* The plan's name under the step, so a month of two plans tells their steps apart. */}
                        <span className="release-plan-calendar-entry-plan">{entry.title}</span>
                      </>
                    )}
                  </span>
                </button>
              ))}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default ReleasePlanCalendar
